import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Menu, X, MessageCircle, Phone } from "lucide-react";
import { openLiveChat } from "../../lib/chat";
import { OLIVEUP_PHONE_E164 } from "../../lib/contact";

const links = [
  { href: "#apply", label: "Apply" },
  { href: "#funding", label: "Funding" },
  { href: "#why-us", label: "Why OliveUp" },
  { href: "#team", label: "The Team" },
  { href: "#faq", label: "FAQ" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToApply = () => {
    setOpen(false);
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-[#F9F8F4]/85 backdrop-blur-xl border-b border-border/60"
          : "bg-transparent"
      }`}
    >
      <div className="container-tight flex h-20 items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center">
          <img
            src="/assets/olive-up-logo.png"
            alt="OliveUp"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {OLIVEUP_PHONE_E164 ? (
            <a
              href={`tel:${OLIVEUP_PHONE_E164}`}
              data-testid="header-call"
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 h-11 text-sm font-semibold text-foreground hover:border-primary hover:text-primary transition-colors"
            >
              <Phone className="h-4 w-4" />
              Call
            </a>
          ) : null}
          <button
            type="button"
            onClick={openLiveChat}
            data-testid="header-chat"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 h-11 text-sm font-semibold text-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <MessageCircle className="h-4 w-4" />
            Live chat
          </button>
          <Button
            data-testid="header-apply-btn"
            onClick={goToApply}
            className="rounded-full bg-accent hover:bg-[#D1664A] text-accent-foreground hover:text-accent-foreground px-6 h-11 font-semibold shadow-[0_4px_14px_0_rgb(224,122,95,0.39)] hover:-translate-y-0.5 transition-all"
          >
            Apply Now
          </Button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden p-2 rounded-full hover:bg-muted text-foreground"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-[#F9F8F4]/95 backdrop-blur-xl">
          <div className="container-tight py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-foreground"
              >
                {l.label}
              </a>
            ))}
            <Button
              data-testid="mobile-apply-btn"
              onClick={goToApply}
              className="rounded-full bg-accent hover:bg-[#D1664A] text-accent-foreground hover:text-accent-foreground h-12 font-semibold mt-2"
            >
              Apply Now
            </Button>
            {OLIVEUP_PHONE_E164 ? (
              <a
                href={`tel:${OLIVEUP_PHONE_E164}`}
                data-testid="mobile-header-call"
                onClick={() => setOpen(false)}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-border bg-background text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
              >
                <Phone className="h-4 w-4" />
                Call us
              </a>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openLiveChat();
              }}
              data-testid="mobile-header-chat"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg border border-border bg-background text-sm font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
            >
              <MessageCircle className="h-4 w-4" />
              Live chat
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
